import React, { useState } from 'react';
import { Stack, Text, Group, Box, SegmentedControl, ScrollArea, Modal, TextInput, Textarea, Select, Button, ActionIcon, Divider, Avatar } from '@mantine/core';
import { Plus, X, Send, User, Headphones, CheckCircle, Clock } from 'lucide-react';
import { TicketCard } from '../components/TicketCard';
import { StatusBadge } from '../components/StatusBadge';
import type { SupportTicket, TicketMessage } from '../types';
// Mock data with messages
const mockTickets: SupportTicket[] = [{
  id: '1',
  subject: 'Payment receipt not received',
  description: "I made the 2nd installment payment last week but haven't received the official receipt yet.",
  status: 'in-progress',
  createdAt: '2024-01-15T10:30:00Z',
  updatedAt: '2024-01-16T14:20:00Z',
  category: 'Payments',
  messages: [{
    id: 'm1',
    ticketId: '1',
    sender: 'user',
    senderName: 'Ahmed Al-Rashid',
    message: "I made the 2nd installment payment last week but haven't received the official receipt yet.",
    timestamp: '2024-01-15T10:30:00Z'
  }, {
    id: 'm2',
    ticketId: '1',
    sender: 'support',
    senderName: 'Sarah Johnson',
    message: 'Thank you for contacting us. We are looking into your payment records and will send you the receipt shortly.',
    timestamp: '2024-01-15T14:20:00Z'
  }, {
    id: 'm3',
    ticketId: '1',
    sender: 'support',
    senderName: 'Sarah Johnson',
    message: 'We have located your payment. The receipt has been sent to your registered email address. Please check your inbox.',
    timestamp: '2024-01-16T14:20:00Z'
  }]
}, {
  id: '2',
  subject: 'Request for site visit',
  description: 'I would like to schedule a site visit to see the construction progress of my unit.',
  status: 'new',
  createdAt: '2024-01-18T09:00:00Z',
  updatedAt: '2024-01-18T09:00:00Z',
  category: 'Site Visit',
  messages: [{
    id: 'm4',
    ticketId: '2',
    sender: 'user',
    senderName: 'Ahmed Al-Rashid',
    message: 'I would like to schedule a site visit to see the construction progress of my unit.',
    timestamp: '2024-01-18T09:00:00Z'
  }]
}, {
  id: '3',
  subject: 'Update contact information',
  description: 'Please update my phone number in your records. New number: +971 50 123 4567',
  status: 'resolved',
  createdAt: '2024-01-10T11:45:00Z',
  updatedAt: '2024-01-12T16:30:00Z',
  category: 'Account',
  messages: [{
    id: 'm5',
    ticketId: '3',
    sender: 'user',
    senderName: 'Ahmed Al-Rashid',
    message: 'Please update my phone number in your records. New number: +971 50 123 4567',
    timestamp: '2024-01-10T11:45:00Z'
  }, {
    id: 'm6',
    ticketId: '3',
    sender: 'support',
    senderName: 'Michael Chen',
    message: 'Your contact information has been updated successfully. Is there anything else we can help you with?',
    timestamp: '2024-01-12T16:30:00Z'
  }]
}, {
  id: '4',
  subject: 'Floor plan modification inquiry',
  description: 'Is it possible to make minor modifications to the kitchen layout? I would like to discuss options.',
  status: 'in-progress',
  createdAt: '2024-01-14T08:15:00Z',
  updatedAt: '2024-01-17T10:00:00Z',
  category: 'Property',
  messages: [{
    id: 'm7',
    ticketId: '4',
    sender: 'user',
    senderName: 'Ahmed Al-Rashid',
    message: 'Is it possible to make minor modifications to the kitchen layout? I would like to discuss options.',
    timestamp: '2024-01-14T08:15:00Z'
  }, {
    id: 'm8',
    ticketId: '4',
    sender: 'support',
    senderName: 'Sarah Johnson',
    message: 'We can certainly discuss modification options. Our design team will review your request and contact you within 48 hours to schedule a consultation.',
    timestamp: '2024-01-17T10:00:00Z'
  }]
}];
export function SupportTickets() {
  const [filter, setFilter] = useState<'all' | 'new' | 'in-progress' | 'resolved'>('all');
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState<SupportTicket | null>(null);
  const [replyMessage, setReplyMessage] = useState('');
  const filteredTickets = mockTickets.filter(ticket => {
    if (filter === 'all') return true;
    return ticket.status === filter;
  });
  const ticketCounts = {
    all: mockTickets.length,
    new: mockTickets.filter(t => t.status === 'new').length,
    'in-progress': mockTickets.filter(t => t.status === 'in-progress').length,
    resolved: mockTickets.filter(t => t.status === 'resolved').length
  };
  const handleViewTicket = (ticket: SupportTicket) => {
    setSelectedTicket(ticket);
    setViewModalOpen(true);
    setReplyMessage('');
  };
  const handleSendReply = () => {
    if (!replyMessage.trim()) return;
    console.log('Send reply:', replyMessage);
    // In real app, this would send the message to the backend
    setReplyMessage('');
  };
  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);
    if (diffHours < 1) return 'Just now';
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };
  return <>
      <Box style={{
      height: '100%',
      position: 'relative'
    }} data-id="element-503">
        <ScrollArea h="100%" offsetScrollbars data-id="element-504">
          <Stack gap="lg" pb={100} data-id="element-505">
            {/* Header */}
            <Box data-id="element-506">
              <Text fw={700} size="xl" c="#1a2332" mb={4} data-id="element-507">
                Support Tickets
              </Text>
              <Text size="sm" c="#64748b" data-id="element-508">
                Get help with your property queries
              </Text>
            </Box>

            {/* Stats */}
            <Group grow gap="sm" data-id="element-509">
              {[{
              label: 'Open',
              count: ticketCounts.new + ticketCounts['in-progress'],
              color: '#3b82f6'
            }, {
              label: 'Resolved',
              count: ticketCounts.resolved,
              color: '#10b981'
            }].map(stat => <Box key={stat.label} p="md" style={{
              backgroundColor: '#f8fafc',
              borderRadius: 12,
              border: '1px solid #e2e8f0'
            }} data-id="element-510">
                  <Text size="xs" c="#64748b" tt="uppercase" fw={500} data-id="element-511">
                    {stat.label}
                  </Text>
                  <Text fw={700} size="xl" c={stat.color} data-id="element-512">
                    {stat.count}
                  </Text>
                </Box>)}
            </Group>

            {/* Filter Tabs */}
            <SegmentedControl value={filter} onChange={value => setFilter(value as typeof filter)} data={[{
            label: `All (${ticketCounts.all})`,
            value: 'all'
          }, {
            label: `New (${ticketCounts.new})`,
            value: 'new'
          }, {
            label: `In Progress (${ticketCounts['in-progress']})`,
            value: 'in-progress'
          }, {
            label: `Resolved (${ticketCounts.resolved})`,
            value: 'resolved'
          }]} fullWidth radius="md" size="xs" styles={{
            root: {
              backgroundColor: '#f1f5f9'
            },
            indicator: {
              backgroundColor: '#1a2332'
            },
            label: {
              fontWeight: 500,
              fontSize: 11,
              padding: '8px 4px',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              '&:hover': {
                backgroundColor: 'rgba(26, 35, 50, 0.05)'
              },
              '&[data-active]': {
                color: 'white',
                '&:hover': {
                  backgroundColor: 'transparent'
                }
              }
            }
          }} data-id="element-513" />

            {/* Ticket List */}
            <Stack gap="sm" data-id="element-514">
              {filteredTickets.length === 0 ? <Box p="xl" style={{
              backgroundColor: '#f8fafc',
              borderRadius: 12,
              textAlign: 'center'
            }} data-id="element-515">
                  <Text c="#64748b" data-id="element-516">No tickets found</Text>
                </Box> : filteredTickets.map(ticket => <TicketCard key={ticket.id} ticket={ticket} onClick={() => handleViewTicket(ticket)} data-id="element-517" />)}
            </Stack>
          </Stack>
        </ScrollArea>

        {/* Floating Action Button */}
        <ActionIcon size={56} radius="xl" variant="filled" onClick={() => setCreateModalOpen(true)} style={{
        position: 'fixed',
        bottom: 80,
        right: 20,
        backgroundColor: '#10b981',
        boxShadow: '0 4px 12px rgba(16, 185, 129, 0.4)'
      }} aria-label="Create new ticket" data-id="element-518">
          <Plus size={24} color="white" data-id="element-519" />
        </ActionIcon>
      </Box>

      {/* Create Ticket Modal */}
      <Modal opened={createModalOpen} onClose={() => setCreateModalOpen(false)} title={<Text fw={700} size="lg" c="#1a2332" data-id="element-521">
            Create New Ticket
          </Text>} radius="md" centered size="md" data-id="element-520">
        <Stack gap="md" data-id="element-522">
          <Select label="Category" placeholder="Select category" data={[{
          value: 'payments',
          label: 'Payments'
        }, {
          value: 'property',
          label: 'Property'
        }, {
          value: 'site-visit',
          label: 'Site Visit'
        }, {
          value: 'documents',
          label: 'Documents'
        }, {
          value: 'account',
          label: 'Account'
        }, {
          value: 'other',
          label: 'Other'
        }]} radius="md" data-id="element-523" />

          <TextInput label="Subject" placeholder="Brief description of your issue" radius="md" data-id="element-524" />

          <Textarea label="Description" placeholder="Provide details about your request..." minRows={4} radius="md" data-id="element-525" />

          <Group justify="flex-end" gap="sm" mt="md" data-id="element-526">
            <Button variant="subtle" color="gray" radius="md" onClick={() => setCreateModalOpen(false)} data-id="element-527">
              Cancel
            </Button>
            <Button color="green" radius="md" styles={{
            root: {
              backgroundColor: '#10b981',
              '&:hover': {
                backgroundColor: '#059669'
              }
            }
          }} data-id="element-528">
              Submit Ticket
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* View Ticket Detail Modal */}
      <Modal opened={viewModalOpen} onClose={() => setViewModalOpen(false)} title={selectedTicket && <Stack gap="xs" data-id="element-530">
              <Group justify="space-between" align="flex-start" data-id="element-531">
                <Text fw={700} size="lg" c="#1a2332" style={{
          flex: 1
        }} data-id="element-532">
                  {selectedTicket.subject}
                </Text>
                <StatusBadge status={selectedTicket.status} data-id="element-533" />
              </Group>
              <Group gap="xs" data-id="element-534">
                <Text size="xs" c="#64748b" tt="uppercase" fw={500} data-id="element-535">
                  {selectedTicket.category}
                </Text>
                <Text size="xs" c="#94a3b8" data-id="element-536">
                  •
                </Text>
                <Text size="xs" c="#64748b" data-id="element-537">
                  Created {formatTimestamp(selectedTicket.createdAt)}
                </Text>
              </Group>
            </Stack>} radius="md" centered size="lg" styles={{
      body: {
        padding: 0
      },
      header: {
        padding: '16px 20px',
        borderBottom: '1px solid #e2e8f0'
      }
    }} data-id="element-529">
        {selectedTicket && <Stack gap={0} data-id="element-538">
            {/* Messages */}
            <ScrollArea h={400} p="md" data-id="element-539">
              <Stack gap="md" data-id="element-540">
                {selectedTicket.messages?.map((message, index) => <Box key={message.id} data-id="element-541">
                    <Group gap="sm" align="flex-start" data-id="element-542">
                      <Avatar radius="xl" size="md" color={message.sender === 'user' ? 'blue' : 'green'} styles={{
                  root: {
                    backgroundColor: message.sender === 'user' ? '#dbeafe' : '#d1fae5'
                  }
                }} data-id="element-543">
                        {message.sender === 'user' ? <User size={18} color="#1a2332" data-id="element-544" /> : <Headphones size={18} color="#1a2332" data-id="element-545" />}
                      </Avatar>
                      <Stack gap={4} style={{
                  flex: 1
                }} data-id="element-546">
                        <Group gap="xs" data-id="element-547">
                          <Text fw={600} size="sm" c="#1a2332" data-id="element-548">
                            {message.senderName}
                          </Text>
                          <Text size="xs" c="#94a3b8" data-id="element-549">
                            {formatTimestamp(message.timestamp)}
                          </Text>
                        </Group>
                        <Box p="sm" style={{
                    backgroundColor: message.sender === 'user' ? '#f8fafc' : '#f0fdf4',
                    borderRadius: 8,
                    border: `1px solid ${message.sender === 'user' ? '#e2e8f0' : '#bbf7d0'}`
                  }} data-id="element-550">
                          <Text size="sm" c="#1a2332" data-id="element-551">
                            {message.message}
                          </Text>
                        </Box>
                      </Stack>
                    </Group>
                    {index < (selectedTicket.messages?.length || 0) - 1 && <Divider my="md" data-id="element-552" />}
                  </Box>)}
              </Stack>
            </ScrollArea>

            {/* Reply Input */}
            {selectedTicket.status !== 'resolved' && <Box p="md" style={{
          borderTop: '1px solid #e2e8f0',
          backgroundColor: '#ffffff'
        }} data-id="element-553">
                <Stack gap="sm" data-id="element-554">
                  <Textarea placeholder="Type your reply..." minRows={3} radius="md" value={replyMessage} onChange={e => setReplyMessage(e.target.value)} styles={{
              input: {
                backgroundColor: '#f8fafc',
                border: '1px solid #e2e8f0',
                '&:focus': {
                  borderColor: '#10b981'
                }
              }
            }} data-id="element-555" />
                  <Group justify="space-between" data-id="element-556">
                    <Text size="xs" c="#64748b" data-id="element-557">
                      Our support team typically responds within 24 hours
                    </Text>
                    <Button leftSection={<Send size={16} data-id="element-559" />} color="green" radius="md" onClick={handleSendReply} disabled={!replyMessage.trim()} styles={{
                root: {
                  backgroundColor: '#10b981',
                  '&:hover': {
                    backgroundColor: '#059669'
                  },
                  '&:disabled': {
                    backgroundColor: '#d1d5db'
                  }
                }
              }} data-id="element-558">
                      Send Reply
                    </Button>
                  </Group>
                </Stack>
              </Box>}

            {/* Resolved Status Message */}
            {selectedTicket.status === 'resolved' && <Box p="md" style={{
          borderTop: '1px solid #e2e8f0',
          backgroundColor: '#f0fdf4'
        }} data-id="element-560">
                <Group gap="sm" data-id="element-561">
                  <CheckCircle size={20} color="#10b981" data-id="element-562" />
                  <Stack gap={2} data-id="element-563">
                    <Text fw={600} size="sm" c="#065f46" data-id="element-564">
                      This ticket has been resolved
                    </Text>
                    <Text size="xs" c="#059669" data-id="element-565">
                      If you need further assistance, please create a new ticket
                    </Text>
                  </Stack>
                </Group>
              </Box>}
          </Stack>}
      </Modal>
    </>;
}